/**
 * What has been imported, and how it went — the Imports tab on Contacts.
 *
 * `GET /v1/contacts/imports` had no caller: the import screen polls a single
 * import by id while it runs and then forgets it. That is fine while somebody
 * is watching, and useless the next morning — "did last night's sync land, and
 * how many rows did it skip" had no answer anywhere in the product.
 *
 * The skipped count is the reason this is worth a screen rather than a log
 * line. An import that skips half its rows reports success, because skipping is
 * what the importer does with a row it must not apply — an unknown consent
 * value, a duplicate address, a contact whose consent it may never change.
 *
 * It is a tab rather than the panel it used to be under the contact list. There
 * the list only had the height left over, so every import listed cost rows of
 * contacts on the screen people use to find somebody; and it showed counts
 * without reasons. The reasons were stored all along and displayed only by the
 * import dialog, before it closed. The drawer here shows them.
 */

import { useEffect } from "react";

import { api } from "../api/client";
import { useAsync, type AsyncState } from "../hooks";
import { Consent } from "../components/Consent";
import { CopyValue } from "../components/CopyValue";
import { SkeletonRows } from "../components/Loading";
import { Icon } from "../components/Sprites";
import { ImportOutcome } from "./ImportContacts";
import type { ContactImport } from "../api/types";

/** The list endpoint returns the most recent fifty and says nothing of the rest. */
export const IMPORT_LIST_LIMIT = 50;

export function isInProgress(record: ContactImport): boolean {
  return record.status === "pending" || record.status === "running";
}

const STATUS_LABEL: Record<ContactImport["status"], string> = {
  pending: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
};

export function ImportList({
  imports,
  selectedId,
  onOpen,
}: {
  imports: AsyncState<ContactImport[]>;
  selectedId?: string;
  onOpen: (id: string) => void;
}) {
  const rows = imports.data ?? [];

  return (
    <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
      <div className="oc-table import-table" style={{ flex: 1, minHeight: 0 }}>
        <div className="oc-table__head" role="row">
          <span>File</span>
          <span>When</span>
          <span className="oc-table__cell--center">Status</span>
          <span className="oc-table__cell--right">Created</span>
          <span className="oc-table__cell--right">Updated</span>
          <span className="oc-table__cell--right">Skipped</span>
        </div>

        <div className="oc-table__body">
          {imports.error && (
            <div className="oc-empty">
              <div className="oc-empty__title">Could not load imports</div>
              <div className="oc-empty__hint">{imports.error.message}</div>
            </div>
          )}

          {imports.loading && rows.length === 0 && <SkeletonRows columns={6} />}

          {!imports.error && !imports.loading && rows.length === 0 && (
            <div className="oc-empty">
              <div className="oc-empty__title">No imports yet</div>
              <div className="oc-empty__hint">
                Each CSV import is listed here with what it created, updated and skipped — and, for
                every skipped row, why.
              </div>
            </div>
          )}

          {rows.map((record) => (
            <ImportRow
              key={record.id}
              record={record}
              selected={record.id === selectedId}
              onOpen={() => onOpen(record.id)}
            />
          ))}
        </div>

        <div className="oc-table__footer">
          <span className="oc-mono-sm">{footer(rows.length)}</span>
        </div>
      </div>
    </div>
  );
}

function ImportRow({
  record,
  selected,
  onOpen,
}: {
  record: ContactImport;
  selected: boolean;
  onOpen: () => void;
}) {
  const skipped = record.skipped_count ?? 0;

  return (
    <div
      className="oc-table__row"
      role="row"
      tabIndex={0}
      aria-selected={selected || undefined}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="oc-truncate" title={record.filename}>
        {record.filename}
      </span>
      <span className="oc-text-secondary" title={record.created_at}>
        {new Date(record.created_at).toLocaleString()}
      </span>
      <span
        className="oc-table__cell--center oc-text-secondary"
        style={record.status === "failed" ? { color: "var(--oc-danger)" } : undefined}
      >
        {STATUS_LABEL[record.status]}
      </span>
      <span className="oc-table__cell--right oc-mono-sm">{(record.created_count ?? 0).toLocaleString()}</span>
      <span className="oc-table__cell--right oc-mono-sm">{(record.updated_count ?? 0).toLocaleString()}</span>
      {/* Skipped is the number worth looking at, so it is the one that changes
          colour when it is not zero. A run that skipped rows still reports
          `completed`. */}
      <span
        className="oc-table__cell--right oc-mono-sm"
        style={skipped > 0 ? { color: "var(--oc-warning)" } : undefined}
      >
        {skipped.toLocaleString()}
      </span>
    </div>
  );
}

function footer(count: number): string {
  if (count === 0) return "No imports";
  if (count >= IMPORT_LIST_LIMIT) return `The ${IMPORT_LIST_LIMIT} most recent imports`;
  return count === 1 ? "1 import" : `${count} imports`;
}

/**
 * One import: what it did, and which rows it skipped and why.
 *
 * Follows a running import itself, so opening one mid-run shows it finish.
 */
export function ImportDrawer({ importId, onClose }: { importId: string; onClose: () => void }) {
  const record = useAsync<ContactImport>(
    (signal) => api.get<ContactImport>(`/v1/contacts/imports/${encodeURIComponent(importId)}`, { signal }),
    [importId],
  );

  const running = record.data ? isInProgress(record.data) : false;
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(record.reload, 2000);
    return () => window.clearInterval(timer);
  }, [running, record.reload]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const data = record.data;

  return (
    <>
      <div className="oc-drawer-scrim" onClick={onClose} />
      <aside className="oc-drawer" role="dialog" aria-modal="true" aria-labelledby="import-title">
        <header className="oc-drawer__header">
          <div style={{ minWidth: 0 }}>
            <div className="oc-label" style={{ marginBottom: 8 }}>
              Import
            </div>
            <h2
              className="oc-headline-md oc-truncate"
              id="import-title"
              style={{ marginBottom: 8 }}
              title={data?.filename}
            >
              {data?.filename ?? (record.loading ? "Loading…" : "Import")}
            </h2>
            <CopyValue value={importId} label="Copy import id" />
          </div>
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--icon oc-btn--sm"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" className="oc-nav__icon" />
          </button>
        </header>

        <div className="oc-drawer__body">
          {record.error && <p className="field-error">{record.error.message}</p>}

          {data && (
            <>
              <ImportOutcome record={data} label="Outcome" />
              <Details record={data} />
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function Details({ record }: { record: ContactImport }) {
  return (
    <section className="drawer-section">
      <span className="oc-label">Details</span>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px var(--oc-space-3)", margin: 0 }}>
        <dt className="oc-body-sm oc-text-secondary">Status</dt>
        <dd className="oc-body-sm" style={{ margin: 0 }}>
          {STATUS_LABEL[record.status]}
        </dd>

        {record.total_rows !== undefined && (
          <>
            <dt className="oc-body-sm oc-text-secondary">Rows in the file</dt>
            <dd className="oc-body-sm" style={{ margin: 0 }}>
              {record.total_rows.toLocaleString()}
            </dd>
          </>
        )}

        <dt className="oc-body-sm oc-text-secondary">Uploaded</dt>
        <dd className="oc-body-sm" style={{ margin: 0 }} title={record.created_at}>
          {new Date(record.created_at).toLocaleString()}
        </dd>

        {record.finished_at && (
          <>
            <dt className="oc-body-sm oc-text-secondary">Finished</dt>
            <dd className="oc-body-sm" style={{ margin: 0 }} title={record.finished_at}>
              {new Date(record.finished_at).toLocaleString()}
            </dd>
          </>
        )}

        <dt className="oc-body-sm oc-text-secondary">Default consent</dt>
        <dd style={{ margin: 0 }}>
          <Consent status={record.consent_status} />
        </dd>
      </dl>
      {/* The question somebody opens an old import to ask is usually why a
          contact's consent is what it is, so the rule is stated where they
          look rather than left for them to infer. */}
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        Default consent applies to rows with no status of their own. An import never changes the
        consent of a contact that already existed.
      </p>
    </section>
  );
}
